/**
 * Manga Chapter Data Service
 * Fetches latest chapter data from multiple sources with fallback chain:
 *   1. AniList (primary, handled by getTotalChapters)
 *   2. MangaUpdates (title-based search → series detail)
 *   3. Jikan/MAL (direct MAL ID lookup)
 *   4. null → shows "?" in UI
 *
 * Data provided by:
 *   - MangaUpdates (https://www.mangaupdates.com)
 *   - Jikan API / MyAnimeList (https://jikan.moe)
 */

const API_BASE = (window.location.port === '3000' ? 'http://localhost:5000' : '') + '/api/mangaupdates';
const CACHE_KEY = 'anitrakt_mangaupdates_cache';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours
const CACHE_TTL_NO_DATA = 2 * 60 * 60 * 1000; // 2 hours — retry sooner for entries that returned no data
const MU_DELAY_MS = 500;   // Delay between MangaUpdates calls
const JIKAN_DELAY_MS = 350; // Jikan allows ~3 req/s

// In-memory cache for the current session
let memoryCache = {};

// ===================== Cache Management =====================

const loadCache = () => {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        const now = Date.now();
        const pruned = {};
        for (const [key, value] of Object.entries(parsed)) {
            if (now - (value.lastFetched || 0) < CACHE_TTL) {
                pruned[key] = value;
            }
        }
        return pruned;
    } catch {
        return {};
    }
};

const saveCache = (cache) => {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (e) {
        console.warn('⚠️ Failed to save chapter cache:', e.message);
    }
};

const initCache = () => {
    if (Object.keys(memoryCache).length === 0) {
        memoryCache = loadCache();
    }
};

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// ===================== MangaUpdates API =====================

const searchMangaUpdatesSeries = async (title) => {
    try {
        const response = await fetch(`${API_BASE}/series/search`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ search: title, perpage: 5 })
        });
        if (!response.ok) return [];
        const data = await response.json();
        return data.results || [];
    } catch {
        return [];
    }
};

const getMangaUpdatesDetail = async (seriesId) => {
    try {
        const response = await fetch(`${API_BASE}/series/${seriesId}`);
        if (!response.ok) return null;
        return await response.json();
    } catch {
        return null;
    }
};

// ===================== Jikan (MAL) API =====================

const getJikanMangaDetail = async (malId) => {
    try {
        const response = await fetch(`${API_BASE}/jikan/manga/${malId}`);
        if (!response.ok) return null;
        const data = await response.json();
        return data.data || null;
    } catch {
        return null;
    }
};

// ===================== Title Matching =====================

const normalizeTitle = (title) => {
    if (!title) return '';
    return title.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();
};

const findBestMatch = (results, targetTitle) => {
    if (!results || results.length === 0) return null;
    const normalizedTarget = normalizeTitle(targetTitle);
    const targetWords = normalizedTarget.split(' ');

    // Pass 1: Exact match on primary title
    for (const result of results) {
        const record = result.record;
        if (!record) continue;
        if (normalizeTitle(record.title) === normalizedTarget) return record;
    }

    // Pass 2: Exact match on associated titles
    for (const result of results) {
        const record = result.record;
        if (!record) continue;
        if (record.associated) {
            for (const assoc of record.associated) {
                if (normalizeTitle(assoc.title) === normalizedTarget) return record;
            }
        }
    }

    // Pass 3: Contains match — but only if titles are similar length
    // Prevents "Ragnarok" (short) from matching "Na Honjaman Level Up Ragnarok" (long)
    for (const result of results) {
        const record = result.record;
        if (!record) continue;
        const normalizedResult = normalizeTitle(record.title);
        const resultWords = normalizedResult.split(' ');

        // Only allow contains match if the shorter title has at least 60% of the longer title's words
        const shorter = Math.min(targetWords.length, resultWords.length);
        const longer = Math.max(targetWords.length, resultWords.length);
        if (shorter / longer < 0.6) continue;

        if (normalizedResult.includes(normalizedTarget) || normalizedTarget.includes(normalizedResult)) {
            return record;
        }
    }

    // Pass 4: Word overlap — match if 80%+ of target words appear in result (or associated titles)
    let bestOverlapRecord = null;
    let bestOverlapRatio = 0;
    for (const result of results) {
        const record = result.record;
        if (!record) continue;

        // Check primary title and all associated titles
        const allTitles = [record.title, ...(record.associated || []).map(a => a.title)];
        for (const title of allTitles) {
            const normalizedCandidate = normalizeTitle(title);
            const candidateWords = new Set(normalizedCandidate.split(' '));
            const matchingWords = targetWords.filter(w => candidateWords.has(w));
            const overlapRatio = matchingWords.length / targetWords.length;

            if (overlapRatio > bestOverlapRatio && overlapRatio >= 0.8) {
                bestOverlapRatio = overlapRatio;
                bestOverlapRecord = record;
            }
        }
    }
    if (bestOverlapRecord) return bestOverlapRecord;

    // No match found — returning wrong series is worse than returning nothing
    return null;
};

// ===================== Unified Fetch =====================

/**
 * Fetch chapter data for a single manga entry using the fallback chain.
 * @param {Object} entry - AniList manga entry
 * @returns {Object} { latestChapter, source, ... }
 */
const fetchChapterDataForEntry = async (entry) => {
    const mediaId = entry.media?.id;
    const malId = entry.media?.idMal;
    const primaryTitle = entry.media?.title?.romaji || entry.media?.title?.english;
    const altTitle = entry.media?.title?.english || entry.media?.title?.romaji;
    const userProgress = entry.progress || 0;

    // --- Source 1: Jikan / MAL (direct ID lookup — most reliable) ---
    if (malId) {
        const jikanData = await getJikanMangaDetail(malId);
        if (jikanData) {
            const chapter = jikanData.chapters;
            if (chapter && chapter >= userProgress) {
                console.log(`  📖 [Jikan] ${primaryTitle}: ${chapter} ch (MAL: ${jikanData.title})`);
                return {
                    latestChapter: chapter,
                    source: 'jikan',
                    malId: malId,
                    jikanTitle: jikanData.title,
                    lastFetched: Date.now()
                };
            }
        }
    }

    // --- Source 2: MangaUpdates (title search → detail) ---
    if (primaryTitle) {
        let results = await searchMangaUpdatesSeries(primaryTitle);
        let bestMatch = findBestMatch(results, primaryTitle);

        if (!bestMatch && altTitle && altTitle !== primaryTitle) {
            results = await searchMangaUpdatesSeries(altTitle);
            bestMatch = findBestMatch(results, altTitle);
        }

        if (bestMatch) {
            const detail = await getMangaUpdatesDetail(bestMatch.series_id);
            let chapter = detail?.latest_chapter ? parseInt(detail.latest_chapter, 10) : null;

            // Also try parsing total chapters from the status field (e.g. "652 Chapters (Ongoing)")
            // MangaUpdates latest_chapter can be per-season, so status field may be more accurate
            if (detail?.status) {
                const statusMatch = detail.status.match(/(\d+)\s*Chapters?/i);
                if (statusMatch) {
                    const statusChapters = parseInt(statusMatch[1], 10);
                    if (!isNaN(statusChapters) && statusChapters > (chapter || 0)) {
                        chapter = statusChapters;
                    }
                }
            }

            if (chapter && !isNaN(chapter) && chapter >= userProgress) {
                console.log(`  📖 [MU] ${primaryTitle}: ${chapter} ch (matched: "${bestMatch.title}")`);
                return {
                    latestChapter: chapter,
                    source: 'mangaupdates',
                    muSeriesId: bestMatch.series_id,
                    muTitle: bestMatch.title,
                    lastFetched: Date.now()
                };
            } else {
                console.log(`  📖 [MU] ${primaryTitle}: no chapter data from matched series "${bestMatch.title}"`);
            }
        } else {
            console.log(`  📖 [MU] ${primaryTitle}: no title match found`);
        }
    }

    // --- No data from any source ---
    console.log(`  📖 [NONE] ${primaryTitle}: no chapter data from any source`);
    return {
        latestChapter: null,
        source: 'none',
        lastFetched: Date.now()
    };
};

// ===================== Batch Fetch =====================

/**
 * Batch fetch latest chapter data for multiple manga entries.
 * Only fetches for entries where AniList's media.chapters is null.
 * @param {Array} mangaEntries - Array of AniList manga entries
 * @returns {Object} Map of anilistId → { latestChapter, source, ... }
 */
export const batchFetchLatestChapters = async (mangaEntries) => {
    initCache();

    const needsFetch = mangaEntries.filter(entry => {
        const mediaId = entry.media?.id;
        if (!mediaId) return false;
        if (entry.media?.chapters) return false;
        if (entry.media?.status === 'NOT_YET_RELEASED') return false;
        const cached = memoryCache[String(mediaId)];
        if (cached) {
            // Use shorter TTL for entries that returned no data — retry sooner
            const ttl = (cached.source === 'none' || cached.source === 'error') ? CACHE_TTL_NO_DATA : CACHE_TTL;
            if ((Date.now() - cached.lastFetched) < ttl) return false;
        }
        return true;
    });

    if (needsFetch.length === 0) {
        console.log('📖 Chapter data: All entries cached, no fetch needed');
        return { ...memoryCache };
    }

    console.log(`📖 Fetching chapter data for ${needsFetch.length} manga (AniList → MU → Jikan)...`);

    let fetched = 0;
    let fromMU = 0;
    let fromJikan = 0;
    let noData = 0;

    for (let i = 0; i < needsFetch.length; i++) {
        const entry = needsFetch[i];
        const mediaId = entry.media.id;
        const cacheKey = String(mediaId);

        try {
            const result = await fetchChapterDataForEntry(entry);
            memoryCache[cacheKey] = result;
            fetched++;

            if (result.source === 'mangaupdates') fromMU++;
            else if (result.source === 'jikan') fromJikan++;
            else noData++;
        } catch (error) {
            console.warn(`⚠️ Failed for: ${entry.media?.title?.romaji}`, error.message);
            memoryCache[cacheKey] = { latestChapter: null, source: 'error', lastFetched: Date.now() };
        }

        // Rate limiting between entries
        if (i < needsFetch.length - 1) {
            await sleep(MU_DELAY_MS);
        }
    }

    console.log(`📖 Chapter data: ${fetched}/${needsFetch.length} done | MU: ${fromMU}, Jikan: ${fromJikan}, none: ${noData}`);

    saveCache(memoryCache);
    return { ...memoryCache };
};

// ===================== Cache Helpers =====================

export const getCachedLatestChapter = (anilistId) => {
    initCache();
    const cached = memoryCache[String(anilistId)];
    if (cached && (Date.now() - cached.lastFetched) < CACHE_TTL) {
        return cached;
    }
    return null;
};

export const clearMangaUpdatesCache = () => {
    memoryCache = {};
    try {
        localStorage.removeItem(CACHE_KEY);
    } catch { /* ignore */ }
};
